import { Link, useLocation } from 'react-router'
import { Home, ChevronLeft, Menu, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { isNavItemActive, navigation } from '@/lib/routes'
import { NAV_ICONS } from '@/lib/nav-icons'

import { BuildInfo } from './build-info'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
  collapsed: boolean
  onToggleCollapse: () => void
}

export function Sidebar({ isOpen, onClose, collapsed, onToggleCollapse }: SidebarProps) {
  const location = useLocation()

  // Add icons to navigation
  const navigationWithIcons = navigation.map(item => ({
    ...item,
    icon: NAV_ICONS[item.key] || Home,
  }))

  return (
    <>
      {/* Desktop sidebar - expandable/collapsible */}
      <aside
        className={cn(
          'hidden h-full flex-col border-r border-border bg-bg transition-all duration-300 ease-in-out md:flex group',
          collapsed ? 'w-16' : 'w-64'
        )}
      >
        <div
          className={cn(
            'py-6 flex items-center',
            collapsed ? 'justify-center px-2' : 'justify-between px-4'
          )}
        >
          {!collapsed && (
            <Link
              to="/properties"
              className="flex items-center transition-opacity hover:opacity-80"
            >
              <img
                src="https://cdn.prod.website-files.com/6695d6ebc8bda059e69cf08b/66a30de7804c6d8bedf73c16_capehost-logo-white.svg"
                alt="Capehost"
                className="h-6"
              />
            </Link>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleCollapse}
            className="h-8 w-8 text-textMuted hover:text-text hover:bg-surface2 focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <Menu className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-4">
          {navigationWithIcons.map(item => {
            const isActive = !item.external && isNavItemActive(location.pathname, item.href)
            const linkContent = (
              <>
                {isActive && !collapsed && (
                  <div className="absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-r-full bg-mint" />
                )}
                <item.icon
                  className={cn('h-5 w-5 shrink-0 transition-colors', isActive && 'text-mint')}
                />
                {!collapsed && (
                  <span className="flex items-center gap-2">
                    {item.name}
                    {item.external && (
                      <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                    )}
                  </span>
                )}
              </>
            )

            if (item.external) {
              return (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'group relative flex items-center rounded-button px-3 py-2.5 text-sm font-medium transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2',
                    collapsed ? 'justify-center' : 'gap-3',
                    'text-textMuted hover:bg-surface2 hover:text-text'
                  )}
                  title={collapsed ? item.name : undefined}
                >
                  {linkContent}
                </a>
              )
            }

            return (
              <Link
                key={item.name}
                to={item.href}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'group relative flex items-center rounded-button px-3 py-2.5 text-sm font-medium transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2',
                  collapsed ? 'justify-center' : 'gap-3',
                  isActive
                    ? 'bg-mintHover text-mint'
                    : 'text-textMuted hover:bg-surface2 hover:text-text'
                )}
                title={collapsed ? item.name : undefined}
              >
                {linkContent}
              </Link>
            )
          })}
        </nav>
        <BuildInfo collapsed={collapsed} />
      </aside>

      {/* Mobile sidebar - drawer */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-50 h-full w-64 flex-col border-r border-border bg-bg transition-transform duration-300 ease-in-out md:hidden',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="px-4 py-6">
          <Link
            to="/properties"
            onClick={onClose}
            className="flex items-center transition-opacity hover:opacity-80"
          >
            <img
              src="https://cdn.prod.website-files.com/6695d6ebc8bda059e69cf08b/66a30de7804c6d8bedf73c16_capehost-logo-white.svg"
              alt="Capehost"
              className="h-6"
            />
          </Link>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-4">
          {navigationWithIcons.map(item => {
            const isActive = !item.external && isNavItemActive(location.pathname, item.href)
            const linkContent = (
              <>
                {isActive && (
                  <div className="absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-r-full bg-mint" />
                )}
                <item.icon className={cn('h-5 w-5 transition-colors', isActive && 'text-mint')} />
                <span className="flex items-center gap-2">
                  {item.name}
                  {item.external && (
                    <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                  )}
                </span>
              </>
            )

            if (item.external) {
              return (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                  className={cn(
                    'group relative flex items-center gap-3 rounded-button px-3 py-2.5 text-sm font-medium transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2',
                    'text-textMuted hover:bg-surface2 hover:text-text'
                  )}
                >
                  {linkContent}
                </a>
              )
            }

            return (
              <Link
                key={item.name}
                to={item.href}
                aria-current={isActive ? 'page' : undefined}
                onClick={onClose}
                className={cn(
                  'group relative flex items-center gap-3 rounded-button px-3 py-2.5 text-sm font-medium transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2',
                  isActive
                    ? 'bg-mintHover text-mint'
                    : 'text-textMuted hover:bg-surface2 hover:text-text'
                )}
              >
                {linkContent}
              </Link>
            )
          })}
        </nav>
        <BuildInfo collapsed={false} />
      </aside>
    </>
  )
}
